import React from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createInventoryDemandLink, getInventoryDemandLink, revokeInventoryDemandLink } from "@/services/demandService";
import "./PropertyDemandLink.scss";

export const demandLinkKey = (inventoryId) => ["propertyDemandLink", Number(inventoryId)];

const linkOf = (response) => response?.data?.link || null;

const copy = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied. Text or email it to the owner.");
  } catch {
    toast.error("Copy did not work here. Select the link and copy it.");
  }
};

const viewsLine = (link) =>
  link.views ? `Opened ${link.views} ${link.views === 1 ? "time" : "times"}` : "Not opened yet";

const PropertyDemandLink = ({ inventoryId }) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: demandLinkKey(inventoryId),
    queryFn: () => getInventoryDemandLink(inventoryId),
    enabled: Boolean(inventoryId),
    retry: false,
  });
  const create = useMutation({
    mutationFn: () => createInventoryDemandLink(inventoryId),
    onSuccess: (response) => {
      queryClient.setQueryData(demandLinkKey(inventoryId), response);
    },
    onError: () => toast.error("The link was not made. Try again."),
  });
  const revoke = useMutation({
    mutationFn: () => revokeInventoryDemandLink(inventoryId),
    onSuccess: (response) => {
      queryClient.setQueryData(demandLinkKey(inventoryId), response);
      toast.success("The link is off. Anyone who opens it sees that it is no longer available.");
    },
    onError: () => toast.error("The link is still on. Try again."),
  });

  if (!inventoryId || query.isError) return null;
  const link = linkOf(query.data);
  const busy = create.isPending || revoke.isPending;

  return (
    <div className="property-demand-link">
      <span className="property-demand-link__text">
        Owner link with demand insights for this&nbsp;home{link ? <span> · {viewsLine(link)}</span> : null}
      </span>
      {link ? (
        <input
          className="property-demand-link__url"
          readOnly
          value={link.url}
          aria-label="Owner link"
          onFocus={(event) => event.target.select()}
        />
      ) : null}
      <span className="property-demand-link__actions">
        {query.isLoading ? null : link ? (
          <>
            <button type="button" className="property-demand-link__action" onClick={() => copy(link.url)}>
              Copy link
            </button>
            <button type="button" className="property-demand-link__action" disabled={busy} onClick={() => revoke.mutate()}>
              Turn off
            </button>
          </>
        ) : (
          <button type="button" className="property-demand-link__action" disabled={busy} onClick={() => create.mutate()}>
            {create.isPending ? "Making the link…" : "Make a link"}
          </button>
        )}
      </span>
    </div>
  );
};

export default PropertyDemandLink;
